import React, { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";
import { AsyncUserSelect } from "components/Common/AsyncSearchSelects";
import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import TableContainer from "components/Common/TableContainer";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import WarningLetterAddEditModal from "pages/AllCommonModal/WarningLetterAddEditModal/warning-letter-add-edit-modal";
import Spinners from "components/Common/Spinner";
import {
    submitUserWarningLetterFilterData,
    fetchUserWarningLetterTableAndFilterSchema,
    addUserWarningLetter,
    updateUserWarningLetter,
} from "model/UserWarningLetterModel";
import { toast } from "react-toastify";
import {
    applyUserAsyncFilterOptionToFormState,
    buildUserAsyncFilterInitialOption,
    getUserAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import dayjs from "dayjs";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import { DEFAULT_PAGE_SIZE } from "helpers/GeneralHelper";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";



const WorkingLetter = () => {
    const { permissionData } = usePermissionContext() || {};
    const { fetchMetadataSchema } = useCommonFilterOptions();
    const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("WarningLetter");
    const initialFilterFetchedRef = useRef(false);
    const [filterForm, setFilterForm] = useState({
        search: "",
        userId: "",
        [getUserAsyncFilterLabelFieldKey()]: "",
        fromDate: "",
        toDate: "",
    });
    const [warningLetterAddEditModalOpen, setWarningLetterAddEditModalOpen] = useState(false);   
    const [modal1, setModal1] = useState(false);
    const [warningLetterData, setWarningLetterData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editableData, setEditableData] = useState(null);
    const [tableMetaData, setTableMetaData] = useState([]);
    const [requestPayload, setRequestPayload] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
    const [paginationMeta, setPaginationMeta] = useState({
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0,
    });
    const toggleViewModal = () => setModal1(!modal1);

    const defaultTableMetaData = [
        { Header: "ID", accessor: "id" },
        { Header: "User", accessor: "userName" },
        { Header: "Date", accessor: "warningLetterDate" },
        { Header: "Note", accessor: "warningLetterNote" },
    ];
    const formatDateToMMDDYYYY = (value) => {
        if (!value) return "";
        const parsedDate = dayjs(value);
        if (!parsedDate.isValid()) return value;
        return parsedDate.format("MM-DD-YYYY");
    };

    const getWarningLetterTableSchema = async () => {
        const response = await fetchMetadataSchema("metadata:userWarningLetter", () =>
            fetchUserWarningLetterTableAndFilterSchema({})
        );
        const columns = response?.data?.table?.columns || [];
        if (Array.isArray(columns) && columns.length) {
            setTableMetaData(
                columns.map((col) => ({
                    Header: col?.title,
                    accessor: col?.field,
                    type: col?.type,
                }))
            );
            return;
        }
        setTableMetaData(defaultTableMetaData);
    };

    const filteredData = useMemo(() => warningLetterData, [warningLetterData]);

    const warningLetterActionPermissions = useMemo(
        () => ({
            canCreate: Boolean(permissionData?.UserWarningLetterAccess),
            canEdit: Boolean(permissionData?.UserWarningLetterAccess),
        }),
        [permissionData]
    );

    const hasAnyWarningLetterRowAction = useMemo(
        () => warningLetterActionPermissions.canEdit,
        [warningLetterActionPermissions]
    );

    const getWarningLetterList = async (payloadOverrides = {}) => {
        setLoading(true);
        const payload = {
            ...requestPayload,
            ...payloadOverrides,
        };
        const response = await submitUserWarningLetterFilterData(payload);
        const responseData = response?.data || {};
        const listData = responseData?.records || responseData || [];
        const mappedData = (Array.isArray(listData) ? listData : []).map((item) => ({
            ...item,
            _originalWarningLetter: { ...item },
            id: item?.id || item?._id || "",
            userId: item?.userId || item?.warningLetterUserId || "",
            userName: item?.userName || item?.user || "",
            warningLetterNote: item?.warningLetterNote || item?.note || "",
            warningLetterDate: formatDateToMMDDYYYY(item?.warningLetterDate || item?.date || ""),
            warningLetterUpdateOn: formatDateToMMDDYYYY(item?.warningLetterUpdateOn),
        }));

        if (response) {
            setWarningLetterData(mappedData);
            setPaginationMeta({
                pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
                pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
                totalPages: Number(responseData?.totalPages) || 0,
                totalRecords: Number(responseData?.totalRecords) || 0,
            });
            setRequestPayload(payload);
        } else {
            setWarningLetterData([]);
        }
        setLoading(false);
    };

    useEffect(() => {
        getWarningLetterTableSchema();
    }, []);

    useEffect(() => {
        if (!settingsReady || initialFilterFetchedRef.current) return;
        initialFilterFetchedRef.current = true;
        const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
        setRequestPayload((previous) => ({ ...previous, pageSize }));
        setPaginationMeta((previous) => ({ ...previous, pageSize }));
        getWarningLetterList({ pageNumber: 1, pageSize });
    }, [settingsReady, settingsPageSize]);

    const handleSearchSubmit = async (value, extraFilters = {}) => {
        const trimmedValue = value?.trim() || "";
        const nextPayload = {
            ...requestPayload,
            pageNumber: 1,
            nameContains: trimmedValue,
            userId: extraFilters?.userId || null,
            fromDate: extraFilters?.fromDate || null,
            toDate: extraFilters?.toDate || null,
        };
        await getWarningLetterList({
            ...nextPayload,
        });
    };

    const handleFilterFieldChange = (e) => {
        const { name, value } = e.target;
        setFilterForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

  const handleEdit = (row) => {
        const raw = row?._originalWarningLetter ?? row;
        setEditableData(
            raw && typeof raw === "object"
                ? {
                      id: raw?.id || raw?._id || "",
                      userId: raw?.userId || raw?.warningLetterUserId || "",
                      userName: raw?.userName || raw?.user || "",
                      warningLetterDate: raw?.warningLetterDate || raw?.date || "",
                      warningLetterNote: raw?.warningLetterNote || raw?.note || "",
                  }
                : null
        );
        setWarningLetterAddEditModalOpen(true);
    };

    const handleAddEditSubmit = async (warningLetterFormData = {}) => {
        const payload = {
            userId: String(warningLetterFormData?.userId || ""),
            warningLetterNote: String(warningLetterFormData?.warningLetterNote || "").trim(),
            warningLetterDate: warningLetterFormData?.warningLetterDate
                ? new Date(warningLetterFormData.warningLetterDate).toISOString()
                : "",
        };
        const response = warningLetterFormData?.id
            ? await updateUserWarningLetter(warningLetterFormData?.id, payload)
            : await addUserWarningLetter(payload);

        if (response?.success || response?.isSuccess) {
            toast.success(response?.message || "Warning Letter saved successfully");
            setWarningLetterAddEditModalOpen(false);
            setEditableData(null);
            await getWarningLetterList({ pageNumber: 1, pageSize: paginationMeta?.pageSize || DEFAULT_PAGE_SIZE });
            return;
        }
        toast.error(response?.message || "Unable to save Warning Letter");
    };

    const handlePageChange = async (page) => {
        await getWarningLetterList({ pageNumber: page });
    };

    const handleRowsPerPageChange = async (rows) => {
        await getWarningLetterList({ pageNumber: 1, pageSize: rows });
  };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />

                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="fw-bold mb-3">Warning Letter</h5>
                                {warningLetterActionPermissions.canCreate ? (
                                    <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                                        <Button
                                            color="primary"
                                            onClick={() => {
                                                setEditableData(null);
                                                setWarningLetterAddEditModalOpen(true);
                                            }}
                                        >
                                            + Create
                                        </Button>
                                    </Col>
                                ) : null}
                            </div>
                            <hr />

                            <LegacyFilterToolbar>
                            <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
                                <Col xs="12" md="4" className="metadata-filter-field metadata-filter-field--text">
                                    <span className="manage-user-filter-label">Search</span>
                                    <div className="metadata-filter-field__control">
                                    <Input
                                        type="text"
                                        name="search"
                                        placeholder="Search by Email/Company etc."
                                        className={LEGACY_FILTER_INPUT_CLASS}
                                        value={filterForm.search}
                                        onChange={(e) => {
                                            const nextValue = e?.target?.value || "";
                                            const nextForm = {
                                                ...filterForm,
                                                search: nextValue,
                                            };
                                            setFilterForm(nextForm);
                                            if (!nextValue.trim()) {
                                                handleSearchSubmit("", nextForm);
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                handleSearchSubmit(filterForm.search, filterForm);
                                            }
                                        }}
                                    />
                                    </div>
                                </Col>
                                <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--autocomplete">
                                    <span className="manage-user-filter-label">User</span>
                                    <div className="metadata-filter-field__control">
                                    <AsyncUserSelect
                                        key={`warning-letter-user-${filterForm.userId}`}
                                        pageName="WarningLetter"
                                        value={filterForm.userId}
                                        initialOption={buildUserAsyncFilterInitialOption(
                                            filterForm.userId,
                                            filterForm[getUserAsyncFilterLabelFieldKey()]
                                        )}
                                        onChange={(option) =>
                                            setFilterForm((prev) =>
                                                applyUserAsyncFilterOptionToFormState(prev, option)
                                            )
                                        }
                                        placeholder="Select User"
                                    />
                                    </div>
                                </Col>
                                <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--date">
                                    <span className="manage-user-filter-label">From date</span>
                                    <div className="metadata-filter-field__control">
                                    <Input
                                        type="date"
                                        name="fromDate"
                                        value={filterForm.fromDate}
                                        onChange={handleFilterFieldChange}
                                        className={LEGACY_FILTER_INPUT_CLASS}
                                    />
                                    </div>
                                </Col>
                                <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--date">
                                    <span className="manage-user-filter-label">To date</span>
                                    <div className="metadata-filter-field__control">
                                    <Input
                                        type="date"
                                        name="toDate"
                                        value={filterForm.toDate}
                                        onChange={handleFilterFieldChange}
                                        className={LEGACY_FILTER_INPUT_CLASS}
                                    />
                                    </div>
                                </Col>
                                <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--actions">
                                    <span className="manage-user-filter-label" aria-hidden="true">&nbsp;</span>
                                    <div className="metadata-filter-toolbar__actions">
                                    <Button
                                        color="primary"
                                        className="w-100 manage-user-search-btn"
                                        onClick={() => {
                                            handleSearchSubmit(filterForm.search, filterForm);
                                        }}
                                    >
                                        <i className="fa fa-search me-1" aria-hidden="true" />
                                        Search
                                    </Button>
                                    </div>
                                </Col>                               
                            </Row>
                            </LegacyFilterToolbar>

                            {!loading && tableMetaData?.length ? (
                                <TableContainer
                                    columns={tableMetaData}
                                    data={filteredData}
                                    isActionRequired={hasAnyWarningLetterRowAction}
                                    onEdit={
                                        warningLetterActionPermissions.canEdit
                                            ? handleEdit
                                            : undefined
                                    }
                                    isEditAccess={warningLetterActionPermissions.canEdit}
                                    isCheckboxLeftSide={false}
                                    isPagination={true}
                                    currentPage={paginationMeta?.pageNumber}
                                    rowsPerPage={paginationMeta?.pageSize}
                                    totalPages={paginationMeta?.totalPages}
                                    totalRecords={paginationMeta?.totalRecords}
                                    onPageChange={handlePageChange}
                                    onPageSizeChange={handleRowsPerPageChange}
                                    pageName={"WarningLetter"}
                                />
                            ) : (
                    //             <Card className="border mb-3">
                    //                 <CardBody>
                    //                     <Spinners setLoading={setLoading} />
                    //     </CardBody>
                    // </Card>
                    <Loaders />
                            )}
                        </CardBody>
                    </Card>

                    <WarningLetterAddEditModal
                        warningLetterAddEditModalOpen={warningLetterAddEditModalOpen}
                        setWarningLetterAddEditModalOpen={setWarningLetterAddEditModalOpen}
                        editableData={editableData}
                        onSubmit={handleAddEditSubmit}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

WorkingLetter.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(WorkingLetter);
